import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../shared/components/alert/Alert';
import { Button } from '../shared/components/button/Button';
import LoadingSpinner from '../shared/components/UIElements/LoadingSpinner';
import { useHttpClient } from '../shared/hooks/http-hook';
import { RightPanelContainer } from './layout/RightPanelContainer';
import { PasswordInput } from './PasswordInput';

export function ResetPassword() {
  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [message, setMessage] = useState<string | undefined>();
  const [password, setPassword] = useState('');
  const [validToken, setValidToken] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const validateResetToken = useCallback(
    async (token) => {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/reset/${token}`
        );
        if (response.token === true) {
          setValidToken(token);
        } else {
          setMessage(t('reset_password.invalid_or_expired_token'));
        }
      } catch (err) {
        /* empty */
      }
    },
    [sendRequest, t]
  );

  useEffect(() => {
    if (window.location.pathname.startsWith('/reset/')) {
      const token = window.location.pathname.split('/')[2];
      validateResetToken(token);
    }
  }, [validateResetToken]);

  const resetPasswordHandler = async (event) => {
    event.preventDefault();
    setMessage(null);
    clearError();
    if (!password?.trim() || !confirmPassword?.trim()) {
      setMessage(t('reset_password.password_required'));
      return;
    }
    if (password !== confirmPassword) {
      setMessage(t('reset_password.passwords_not_the_same'));
      return;
    }

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/resetPassword/${validToken}`,
        'POST',
        JSON.stringify({
          password,
        })
      );

      if (response.success === true && response.message) {
        setPassword('');
        setConfirmPassword('');
        navigate('/sign-in', {
          state: { successMessage: response.message || t('reset_password.success') },
        });
      }
    } catch (err) {
      setMessage(t('reset_password.error')); /* empty */
    }
  };

  return (
    <RightPanelContainer type="resetPassword">
      <h1>{t('R022')}</h1>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <form onSubmit={resetPasswordHandler}>
          <PasswordInput
            visible
            handlePassword={(value) => setPassword(value)}
            confirmPassword={confirmPassword}
            setMessage={setMessage}
            clearError={clearError}
            onEnterKeyPress={() => {
              if (!isLoading && validToken) {
                const syntheticEvent = {
                  // eslint-disable-next-line @typescript-eslint/no-empty-function -- stub matching the FormEvent shape resetPasswordHandler expects
                  preventDefault: () => {},
                } as React.FormEvent;
                resetPasswordHandler(syntheticEvent);
              }
            }}
          />
          <PasswordInput
            visible={false}
            handleConfirmPassword={(value) => setConfirmPassword(value)}
            password={password}
            setMessage={setMessage}
            clearError={clearError}
            onEnterKeyPress={() => {
              if (!isLoading && validToken) {
                const syntheticEvent = {
                  // eslint-disable-next-line @typescript-eslint/no-empty-function -- stub matching the FormEvent shape resetPasswordHandler expects
                  preventDefault: () => {},
                } as React.FormEvent;
                resetPasswordHandler(syntheticEvent);
              }
            }}
          />
          {(message?.length > 0 || error?.length > 0) && (
            <Alert
              type="error"
              message={message || error}
              id="reset-password-error"
              isVisible
              isAssessive
            />
          )}
          <Button type="submit" id="continue" isDisabled={validToken === '' || validToken === null}>
            {t('R042')}
          </Button>
        </form>
      )}
    </RightPanelContainer>
  );
}
